import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'

import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import ItemCardSkeleton from 'tripPlanner/components/Skeleton/ItemCardSkeleton'
import TripPageContainer from 'tripPlanner/components/TripPageContainer'
import { usePublicTrip } from 'tripPlanner/hooks/api/publicTrip'
import TripPlannerFilters from 'tripPlanner/components/TripItemFilters/TripPlannerFilters'
import type { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { getTripFilters } from 'tripPlanner/utils/selectors'
import { bySummaryRank } from 'tripPlanner/utils/sortable'
import {
  filterTripItems,
  getNonEmptyProductFilters,
} from 'tripPlanner/utils/summary'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import DetailItemCard from 'tripPlanner/components/DetailItemCard/DetailItemCard'

const TopSection = styled.div`
  padding: 0 ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(40)};
  }
`

const ItemsContainer = styled(Group)`
  background-color: ${(props) => props.theme.palette.neutral.default.six};
`

interface Props extends RouteComponentProps {
  trip: PublicFullTrip
}

function PublicTripSummary({ trip }: Props) {
  const intl = useIntl()
  const { isLoading, isFetched, data: publicTrip } = usePublicTrip(trip.id)

  const filters = useAppSelector(getTripFilters)

  const productFilters = useMemo(() => {
    return getNonEmptyProductFilters(publicTrip?.items)
  }, [publicTrip?.items])

  const filteredItems = useMemo(() => {
    const filtered = filterTripItems(publicTrip?.items ?? [], filters)
    return [...filtered].sort(bySummaryRank)
  }, [publicTrip?.items, filters])

  if (isLoading && !isFetched) {
    return (
      <TripPageContainer>
        <ItemsContainer
          direction="vertical"
          gap={8}
        >
          <ItemCardSkeleton />
          <ItemCardSkeleton />
          <ItemCardSkeleton />
        </ItemsContainer>
      </TripPageContainer>
    )
  }

  const totalItemCount = publicTrip?.items.length ?? 0

  return (
    <TripPageContainer>
      <Group
        direction="vertical"
        horizontalAlign="stretch"
        verticalAlign="space-between"
        gap={20}
      >
        {totalItemCount === 0 && (
          <TopSection>
            <Group
              direction="vertical"
              verticalAlign="space-between"
              horizontalAlign="start"
              gap={8}
            >
              <Heading colour="neutral-three" variant="heading4">
                {intl.formatMessage({
                  id: 'Uw8Xk0IA6DaqRxiB-z41',
                  defaultMessage: 'It’s lonely in here',
                  description: 'Empty state heading when public trip has no items in the trip planner',
                })}
              </Heading>
              <BodyText colour="neutral-three" variant="medium">
                {intl.formatMessage({
                  id: 'J3yrTMc7B-mO3Q1cG286',
                  defaultMessage: 'No trip items so far',
                  description: 'Empty state message when public trip has no items in the trip planner',
                })}
              </BodyText>
            </Group>
          </TopSection>
        )}
        {totalItemCount > 0 && (
          <>
            <TripPlannerFilters
              selectedFilters={filters}
              availableSubFilters={productFilters}
              itemCount={totalItemCount}
              style="scroll"
            />
            <ItemsContainer direction="vertical" gap={8}>
              {filteredItems.map((item) => (
                <DetailItemCard
                  key={item.id}
                  // This cast to TripItem shouldn't be here, but the item card
                  // components don't accept PublicTripItem. TODO: fix that.
                  item={item as TripItem}
                  variant="tripSummary"
                />
              ))}
            </ItemsContainer>
          </>
        )}
      </Group>
    </TripPageContainer>
  )
}

export default withRouter(PublicTripSummary)
